import * as React from 'react';

import {Button} from 'shells/button/button';
import {PaginatedTable} from 'shells/table/paginated-table';
import type {Column} from 'shells/table/table-types';
import {Body} from 'shells/typography/body';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {ExternalMeetingFragment} from 'nutshell-graphql-types';

import {appHistory} from '../../history';

import {AiSummaryTruncated} from './tables/ai-summary-truncated';
import {ButtonCell} from './tables/button-cell';
import {ActivityParticipants} from './tables/cells/activity-participants';
import {ExternalMeetingParticipants} from './tables/cells/external-meeting-participants';
import {Direction} from './tables/direction';
import {DurationWithRecording} from './tables/duration-with-recording';
import {HeaderAiSummary} from './tables/headers/ai-summary';
import {stripCallPrefixIfNeeded} from './tables/helpers';
import {Participant} from './tables/participant';
import type {ExternalServiceTypeFrontend} from './tables/types';

type Props = {
    meetings: ExternalMeetingFragment[];
    onAssociateMeetingWithActivity?: (id: string) => Promise<any>;
    onRemoveMeeting?: (id: string) => Promise<any>;
    fetchNextPage: () => Promise<any>;
    fetchPreviousPage: () => Promise<any>;
    totalCount: number;
    pageSize: number;
    isLoading: boolean;
    serviceType: ExternalServiceType | ExternalServiceTypeFrontend;
    emptyState: React.ReactNode;
    shouldShowSummaryColumn: boolean;
};

export function MeetingsTable(props: Props) {
    const {onAssociateMeetingWithActivity, onRemoveMeeting} = props;

    const columns: Column<ExternalMeetingFragment>[] = React.useMemo(() => {
        const cols: Column<ExternalMeetingFragment>[] = [
            {
                // @ts-expect-error
                id: 'name',
                Header: 'Name',
                accessor: (row: ExternalMeetingFragment) => row,
                dataType: 'component',
                width: 150,
                minWidth: 150,
                disableSortBy: true,
                shouldTruncate: true,
                Cell: ({cell}: any) => {
                    return cell.value.activity ? (
                        <Button
                            variant='text-primary'
                            onClick={() => {
                                // This should always be defined, but safety in case
                                const activityId = cell.value.activity.id;
                                if (activityId) {
                                    appHistory.replace({
                                        ...appHistory.location,
                                        hash: `/${activityId}`,
                                        state: {modal: true},
                                    });
                                }
                            }}
                            noPadding={true}
                        >
                            {stripCallPrefixIfNeeded(cell.value.activity.name)}
                        </Button>
                    ) : (
                        <Body size='small' isItalic={!cell.value.topic}>
                            {cell.value.topic || 'Untitled meeting'}
                        </Body>
                    );
                },
            },
            {
                // @ts-expect-error
                id: 'participant',
                Header: props.serviceType === 'PHONES' ? 'Participant' : 'Attendees',
                dataType: 'component',
                disableSortBy: true,
                width: 150,
                minWidth: 150,
                maxWidth: 150,
                accessor: (row: ExternalMeetingFragment) => row,
                Cell: (cell: {
                    cell: {
                        value: ExternalMeetingFragment;
                    };
                }) => {
                    const value = cell.cell.value;

                    // If no activity, show external meeting participants
                    if (!value.activity) {
                        return <ExternalMeetingParticipants participants={value.participants} />;
                    }

                    // Find contact or external attendee participants
                    const relevantParticipants = value.activity.participants.filter(
                        (p) => p.type === 'contacts' || p.type === 'activityExternalAttendees'
                    );

                    // If exactly one participant, show single Participant component
                    if (relevantParticipants.length === 1) {
                        const participant = relevantParticipants[0];

                        return (
                            <Participant
                                // @ts-expect-error
                                name={participant.name}
                                // @ts-expect-error
                                htmlUrlPath={participant.htmlUrlPath}
                                // @ts-expect-error
                                primaryPhoneNumber={participant.primaryPhoneNumber}
                                // @ts-expect-error
                                primaryEmailAddress={participant.primaryEmailAddress}
                            />
                        );
                    }

                    // If multiple participants, show ActivityParticipants component
                    return <ActivityParticipants participants={relevantParticipants} />;
                },
            },
            {
                // @ts-expect-error
                id: 'startTime',
                Header: 'Date',
                accessor: (row: ExternalMeetingFragment) =>
                    row.activity ? row.activity.startTime : row.startTime,
                dataType: 'date-two-line',
            },
            {
                // @ts-expect-error
                id: 'duration',
                Header: 'Duration',
                accessor: (row: ExternalMeetingFragment) => row,
                dataType: 'component',
                disableSortBy: true,
                shouldTruncate: true,
                width: 150,
                minWidth: 150,
                maxWidth: 150,
                Cell: ({cell}: any) => {
                    return <DurationWithRecording meeting={cell.value} />;
                },
            },
        ];

        if (props.serviceType === 'PHONES') {
            cols.unshift({
                // @ts-expect-error
                id: 'direction',
                dataType: 'component',
                accessor: (row: ExternalMeetingFragment) => row,
                width: 60,
                minWidth: 60,
                maxWidth: 60,
                Cell: (cell: {
                    cell: {
                        value: ExternalMeetingFragment;
                    };
                }) => {
                    const type = cell.cell.value.type;
                    if (type !== 'PHONE_CALL_INBOUND' && type !== 'PHONE_CALL_OUTBOUND') {
                        return null;
                    }

                    return <Direction type={type} />;
                },
            });
        }

        if (props.shouldShowSummaryColumn) {
            const nameIndex = cols.findIndex((c: any) => c.id === 'name');
            cols.splice(nameIndex + 1, 0, {
                // @ts-expect-error
                id: 'summary',
                Header: () => <HeaderAiSummary title='Summary' />,
                accessor: (row: ExternalMeetingFragment) => row,
                dataType: 'component',
                width: 150,
                disableSortBy: true,
                Cell: ({
                    cell,
                }: {
                    cell: {
                        value: ExternalMeetingFragment;
                    };
                }) => {
                    const meeting = cell.value;

                    if (meeting.recordingStatus === 'NONE') {
                        return (
                            <Body size='small' color='light'>
                                {props.serviceType === 'PHONES'
                                    ? 'Call not recorded'
                                    : 'Meeting not recorded'}
                            </Body>
                        );
                    }

                    const isGenerating =
                        meeting.transcriptStatus === 'PROCESSING' ||
                        meeting.recordingStatus === 'PROCESSING';

                    const summaryData = meeting.activity
                        ? meeting.activity.summary
                        : meeting.transcriptionSummary;

                    return (
                        <AiSummaryTruncated
                            summary={summaryData}
                            title='Call summary'
                            isGenerating={isGenerating && !summaryData}
                        />
                    );
                },
            });
        }

        // Only add button cell if callbacks are provided (for unlogged meetings)
        if (onAssociateMeetingWithActivity && onRemoveMeeting) {
            cols.push({
                // @ts-expect-error
                id: 'buttons',
                Header: '',
                accessor: (row: ExternalMeetingFragment) => row,
                dataType: 'component',
                width: 100,
                disableSortBy: true,
                hasRightBorder: true,
                Cell: ({cell}: any) => (
                    <ButtonCell
                        cell={cell}
                        onAssociateMeetingWithActivity={onAssociateMeetingWithActivity}
                        onRemoveMeeting={onRemoveMeeting}
                        serviceType={props.serviceType}
                        isProcessing={cell.value.isProcessing}
                    />
                ),
            });
        }

        return cols;
    }, [
        onAssociateMeetingWithActivity,
        onRemoveMeeting,
        props.serviceType,
        props.shouldShowSummaryColumn,
    ]);

    return (
        <PaginatedTable
            columns={columns}
            data={props.meetings}
            fetchNextPage={props.fetchNextPage}
            fetchPreviousPage={props.fetchPreviousPage}
            pageSize={props.pageSize}
            totalCount={props.totalCount}
            updateUrlParamsOnSortChange={() => {}}
            fullWidth={true}
            isLoading={props.isLoading}
            hasHeader={props.totalCount > 0}
            emptyState={props.emptyState}
            itemSize={60}
        />
    );
}
